import React, { useEffect, useRef, useState } from 'react';
import { useScreenBreakpoint } from 'consts/breakpoints';
import * as Styled from './styled';
import ArrowLeft from 'assets/svg/arrow-shadow-left.svg';
import ArrowRight from 'assets/svg/arrow-shadow-right.svg';
import { getInfiniteNextIndex, getInfinitePrevIndex } from 'utils/gallery';
import { createPanResponder } from 'helpers/pan-responder';
import GallerySection from '../../components/MobileGallery';
import { ForwardRefProps, InfiniteScroll, InfinityDirection } from '../../components/InfinityGallery';
import { DealType } from 'utils/entities';
import { routeByPoiType } from 'utils/marketplaceRoutes';
import { Action, Direction, EventProps, OfficeBulletinType, PageDetails } from 'screens/AgentOfficePages/types';
import { SectionTitle } from 'screens/AgentOfficePages/styled';
import { useLocale } from 'locale';
import withEvents from 'analytics/withEvents';
import { noop } from 'lodash';
import { useRoute } from 'react-router5';
import { Route } from 'config/routes';
import { getAgentOfficeCategory } from 'screens/AgentOfficePages/helper';


export interface Images {
  img: string;
  address: string;
  id: string;
  dealType: DealType;
  bulletinType: OfficeBulletinType;
}

interface GalleryProps {
  images: Images[];
  grayShadow?: boolean;
  withTitle?: boolean;
  pageDetails?: PageDetails;
  onCarouselScroll?: (props: EventProps) => void;
}

const GalleryBase: React.FC<GalleryProps> = ({ images, grayShadow, withTitle, onCarouselScroll = noop, pageDetails }) => {
  const { t } = useLocale();
  const isMobile = useScreenBreakpoint() === 1;
  const isTablet = useScreenBreakpoint() === 2;
  const isDesktop = useScreenBreakpoint() >= 3;
  const [ slideIndex, setSlideIndex ] = useState(0);
  const infinityCarouselRef = useRef<ForwardRefProps>(null);
  const [ isHovered, setIsHovered ] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);
  let interval: NodeJS.Timeout = null;
  const isSingle = images.length === 1;
  const isLessThanScreen = isTablet && images.length <= 2 || isDesktop && images.length <= 3;
  const { route } = useRoute();
  const isOfficePage = route.name === Route.OfficePage;
  const event = getAgentOfficeCategory(isOfficePage) === 'office' ? 'office_carousel_scroll' : 'agent_carousel_scroll';
  const category = getAgentOfficeCategory(isOfficePage);
  const eventHandler = (direction: Direction, action: Action) => {
    onCarouselScroll({
      event,
      category,
      direction,
      action,
      pageDetails,
    });
  };

  const onPrevSlide = () => {
    const total = images.length;
    const nextSlideIndex = getInfinitePrevIndex(slideIndex, total);
    setSlideIndex(nextSlideIndex);
    eventHandler(Direction.Right, Action.Swipe);
  };

  const onNextSlide = () => {
    const total = images.length;
    const nextSlideIndex = getInfiniteNextIndex(slideIndex, total);
    setSlideIndex(nextSlideIndex);
    eventHandler(Direction.Left, Action.Swipe);
  };

  const panHandlers = createPanResponder({
    onStart: () => setIsHovered(true),
    onMove: () => setIsHovered(true),
    onEnd: () => setIsHovered(false),
  });

  const handleChangeInfinityDirection = (d: InfinityDirection, skipOnScrollCb?: boolean) => {
    if (infinityCarouselRef.current) infinityCarouselRef.current.move(d, skipOnScrollCb);
  };

  const onScrollHandler = (d: InfinityDirection) => {
    eventHandler(d === InfinityDirection.Next ? Direction.Left : Direction.Right, isDesktop ? Action.Click : Action.Swipe);
  };

  const handleSlideIndex = (d: number) => {
    setSlideIndex((prevIdx: number) => {
      eventHandler(prevIdx > d ? Direction.Left : Direction.Right, isDesktop ? Action.Click : Action.Swipe);
      return d;
    });
  };

  useEffect(() => {
    if (isHovered) {
      clearInterval(interval);
    }
    else {
      interval = setInterval(() => {
        if (isMobile) {
          const total = images.length;
          setSlideIndex(prevState => {
            return getInfiniteNextIndex(prevState, total);
          });
        }
        else {
          handleChangeInfinityDirection(InfinityDirection.Prev, true);
        }
      }, 5000);
    }
    return () => clearInterval(interval);
  }, [ isHovered, isMobile ]);

  const singleContent = (
    <Styled.ItemWrapper isSingle={isSingle} routeName={routeByPoiType(images[0].bulletinType)} routeParams={{ id: images[0].id, dealType: images[0].dealType, tracking_event_source: `${getAgentOfficeCategory(isOfficePage)}_page_gallery` }}>
      <img src={images[0].img} />
      <Styled.ItemTitle>{images[0].address}</Styled.ItemTitle>
    </Styled.ItemWrapper>
  );

  const lessThanScreenContent = (
    <Styled.LessThanScreenWrapper>
      {images.map(({ img, address, id, dealType, bulletinType }, i) => (
        <Styled.ItemWrapper key={i} routeName={routeByPoiType(bulletinType)} routeParams={{ id, dealType, tracking_event_source: `${getAgentOfficeCategory(isOfficePage)}_page_gallery` }} hideLinkParams>
          <img src={img} />
          <Styled.ItemTitle>{address}</Styled.ItemTitle>
        </Styled.ItemWrapper>
      ))}
    </Styled.LessThanScreenWrapper>
  );

  const defaultContent = (
    <InfiniteScroll
      elementHeight={360}
      elementWidth={360}
      disableSwipe={isDesktop}
      spacing={isDesktop ? 32 : 16}
      ref={infinityCarouselRef}
      onScrollHandler={onScrollHandler}
    >
      {images.map(({ img, address, id, dealType, bulletinType }, i) => (
        <Styled.InfinityItemWrapper key={i} routeName={routeByPoiType(bulletinType)} routeParams={{ id, dealType, source: 'office_page', tracking_event_source: `${getAgentOfficeCategory(isOfficePage)}_page_gallery` }} hideLinkParams>
          <img src={img} />
          <Styled.ItemTitle>{address}</Styled.ItemTitle>
        </Styled.InfinityItemWrapper>
      ))}
    </InfiniteScroll>
  );

  let galleryContent =  defaultContent;

  if (isSingle) {
    galleryContent = singleContent;
  }
  else if (isLessThanScreen) {
    galleryContent = lessThanScreenContent;
  }

  return (
    <>
      <Styled.SectionWrapper
        isMobile={isMobile}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        ref={sectionRef}
        {...panHandlers}
      >
        {isMobile && images.length ? (
          <GallerySection
            images={images}
            slideIndex={slideIndex}
            setSlideIndex={handleSlideIndex}
            onPrevSlide={onPrevSlide}
            onNextSlide={onNextSlide}
          />
        ) : (
          <Styled.CarouselWrapper>
            {withTitle ? <SectionTitle data-acc-heading={isOfficePage ? 'h2' : undefined}>{t('developerPage.gallery.title')}</SectionTitle> : null}
            {galleryContent}
            {isSingle || isLessThanScreen || !isDesktop ? null : (
              <>
                <Styled.ArrowLeftWrapper
                  className="arrows"
                  onClick={isMobile ? onNextSlide : () => handleChangeInfinityDirection(InfinityDirection.Next)}
                  grayShadow={grayShadow}
                >
                  <ArrowLeft />
                </Styled.ArrowLeftWrapper>
                <Styled.ArrowRightWrapper
                  className="arrows"
                  onClick={isMobile ? onPrevSlide : () => handleChangeInfinityDirection(InfinityDirection.Prev)}
                  grayShadow={grayShadow}
                >
                  <ArrowRight />
                </Styled.ArrowRightWrapper>
              </>
            )}
          </Styled.CarouselWrapper>
        )}
      </Styled.SectionWrapper>
    </>
  );
};

export const Gallery = withEvents<GalleryProps>(sendEvent => ({
  onCarouselScroll(props: EventProps) {
    sendEvent(props.event, props.category, {
      ...props.pageDetails,
      event: {
        carousel_type: 'gallery',
        direction: props.direction,
        action: props.action,
      },
    });
  },
}))(GalleryBase);
